import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const MOBILE_BREAKPOINT_QUERY = '(max-width: 749px)';

/**
 * Wraps a single above-the-fold autoplaying hero <video> + its permanent
 * poster image. Each instance is scoped to its own video only (`this`, the
 * custom element, not `document`) -- a hidden desktop video's autoplay
 * failure or ready-state can never affect a different instance's poster or
 * video.
 *
 * The video always renders on top of a permanent, always-visible poster
 * image, starting at opacity 0 so the poster is what's on screen while the
 * video is still fetching -- never a blank/black frame. It only fades in
 * once it has an actual displayable frame; a hidden or never-playing video
 * simply never earns that class, leaving the poster as the permanent result
 * with no controls, no play button, and no black frame.
 *
 * @extends Component
 */
export class HeroVideoComponent extends Component {
  #mobileQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
  #onBreakpointChange = () => this.#syncActiveState();

  connectedCallback() {
    super.connectedCallback();

    const video = this.querySelector('video');
    if (!video) return;

    // Set explicitly rather than relying solely on the server-rendered
    // attributes -- some mobile browsers only honor autoplay when these are
    // also true as element properties at the time play() is requested.
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.removeAttribute('controls');
    video.controls = false;

    this.#revealWhenFrameReady(video);

    this.#mobileQuery.addEventListener('change', this.#onBreakpointChange);
    this.#syncActiveState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mobileQuery.removeEventListener('change', this.#onBreakpointChange);
  }

  /**
   * Only the wrapper that's actually visible for the current breakpoint
   * (the desktop vs. mobile `<hero-video>`, toggled by CSS) should attempt
   * playback. This re-runs on every desktop/mobile breakpoint crossing so
   * resizing or rotating always re-evaluates which one is active.
   */
  #syncActiveState() {
    const video = this.querySelector('video');
    if (!video) return;

    const isActive = getComputedStyle(this).display !== 'none';

    if (!isActive) {
      video.pause();
      return;
    }

    if (window.matchMedia(REDUCED_MOTION_QUERY).matches) {
      video.pause();
      video.removeAttribute('autoplay');
      return;
    }

    const playAttempt = video.play();

    // Autoplay can be rejected (iOS Low Power Mode, Data Saver, browser
    // policy, etc). Never add controls or a play button for this -- the
    // poster is a permanent sibling element, not part of the video, and
    // simply stays visible since the video never earns the "is-ready"
    // class without an actual composited frame.
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {});
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #revealWhenFrameReady(video) {
    const reveal = () => video.classList.add('is-ready');

    // requestVideoFrameCallback confirms a frame has actually been
    // presented for compositing -- a stronger guarantee than `loadeddata`
    // or `canplay` (which only mean data is available, not that it's
    // painted). Fall back to `canplay` where it's unsupported.
    if ('requestVideoFrameCallback' in video) {
      video.requestVideoFrameCallback(reveal);
      return;
    }

    // A frame may already be available (e.g. cached video), in which case
    // `canplay` won't fire again -- reveal immediately in that case instead
    // of waiting on a listener that will never run.
    if (video.readyState >= 3) {
      reveal();
      return;
    }

    video.addEventListener('canplay', reveal, { once: true });
  }
}

if (!customElements.get('hero-video')) {
  customElements.define('hero-video', HeroVideoComponent);
}
